/**
 * Live-tennis V1c — match-level pair/residual inventory math (pure).
 *
 * The manual-trading port of the vanilla MM report's pairing model
 * (relay/src/services/vanillaMm/report.ts computeGameReport): a two-outcome
 * match with complementary sibling Kalshi markets settles every AWAY+HOME
 * contract pair at exactly 100¢ whoever wins, so
 *
 *   paired   = min(qtyAway, qtyHome)
 *   blended  = avgAway + avgHome            (per-pair cost, cents)
 *   locked   = paired × (100 − blended)     (GROSS, pre-fee)
 *   residual = |qtyAway − qtyHome|          (directional exposure)
 *
 * Differences from the bot report, on purpose:
 *   • Inputs are OPEN position aggregates (netCount + open cost basis), not
 *     lifetime buys — this card answers "what am I holding right now".
 *   • Side normalization: long-AWAY = YES on the away market OR NO on the
 *     home market (and mirrored for HOME). Position rows are side-native, so
 *     a NO row's basis is already the NO cost and sums directly.
 *   • computeGamePairSummary is GROSS, and stays that way: every figure it
 *     returns is pre-fee. #512 approved netting exactly ONE of them for
 *     display — the paired edge, via the separate `pairedEntryFeeCents` below,
 *     subtracted by the caller. Cost basis, blended pair cost, paired quantity
 *     and both residual figures remain gross by contract.
 *
 * "Blended" survives the net/gross switch: avg-based pair cost is still an
 * approximation when fills span prices, so callers label output
 * "blended · gross" or "blended · net" — never "locked" in either state.
 */

import { estimateAggregateFeeCents } from '@/lib/pricing/fees';

export interface PairPositionRow {
  ticker: string;
  side: 'yes' | 'no';
  /** Open contracts (unsigned). */
  position: number;
  /** Exact open-share basis in cents when known (relay ledger). */
  costBasisCents?: number;
  /** Side-native whole-cent avg — the basis fallback. */
  averagePrice: number;
  /** Entry fees Kalshi actually billed on this row's OPEN share (relay
   *  ledger). `undefined` means the buy fees aren't fully known for the row,
   *  never that they were zero. */
  openFeesCents?: number;
}

export interface OutcomeSummary {
  /** Open contracts long this outcome (across both sibling markets). */
  qty: number;
  /** Open cost basis in cents (fee-exclusive). */
  costCents: number;
  /** costCents / qty — may be fractional. Null when qty is 0. */
  avgCents: number | null;
  /** Actual open-share entry fees summed across this outcome's rows. Null when
   *  ANY contributing row withheld its fees — a partial sum would understate
   *  the outcome, which is worse than admitting the number isn't known. */
  openFeesCents: number | null;
}

export interface GamePairSummary {
  away: OutcomeSummary;
  home: OutcomeSummary;
  pairedContracts: number;
  unpairedContracts: number;
  heavySide: 'away' | 'home' | null;
  /** avgAway + avgHome per pair — an approximation when fills span prices
   *  and quantities are unequal. Label as BLENDED, never "locked". */
  blendedPairCostCents: number | null;
  /** paired × (100 − blended). Gross, pre-fee. 0 when nothing is paired. */
  grossPairedEdgeCents: number;
  /** Residual downside: unpaired × heavy avg (loses → basis gone). */
  openRiskCents: number;
  /** Residual upside: unpaired × (100 − heavy avg). */
  openUpsideCents: number;
}

/** Fold side-native position rows for a match's two sibling Kalshi markets
 *  into outcome-level pair context. Returns null when the rows carry no open
 *  contracts on either market (callers render nothing). */
export function computeGamePairSummary(
  rows: readonly PairPositionRow[],
  awayTicker: string,
  homeTicker: string,
): GamePairSummary | null {
  let awayQty = 0;
  let awayCost = 0;
  let homeQty = 0;
  let homeCost = 0;
  // Actual entry fees per outcome. One row withholding its fees poisons its
  // whole outcome — summing the rest would quietly report a fee smaller than
  // the trader was billed.
  let awayFees: number | null = 0;
  let homeFees: number | null = 0;

  for (const row of rows) {
    if (row.position <= 0) continue;
    const isAwayMarket = row.ticker === awayTicker;
    const isHomeMarket = row.ticker === homeTicker;
    if (!isAwayMarket && !isHomeMarket) continue;
    const cost = row.costBasisCents ?? row.averagePrice * row.position;
    // YES on a market backs that market's outcome; NO backs its sibling.
    const backsAway = (isAwayMarket && row.side === 'yes') || (isHomeMarket && row.side === 'no');
    if (backsAway) {
      awayQty += row.position;
      awayCost += cost;
      awayFees = row.openFeesCents == null || awayFees == null ? null : awayFees + row.openFeesCents;
    } else {
      homeQty += row.position;
      homeCost += cost;
      homeFees = row.openFeesCents == null || homeFees == null ? null : homeFees + row.openFeesCents;
    }
  }

  if (awayQty <= 0 && homeQty <= 0) return null;

  const avgAway = awayQty > 0 ? awayCost / awayQty : null;
  const avgHome = homeQty > 0 ? homeCost / homeQty : null;
  const paired = Math.min(awayQty, homeQty);
  const unpaired = Math.abs(awayQty - homeQty);
  const heavySide = awayQty > homeQty ? 'away' : homeQty > awayQty ? 'home' : null;
  const heavyAvg = heavySide === 'away' ? (avgAway ?? 0) : heavySide === 'home' ? (avgHome ?? 0) : 0;
  const blended = paired > 0 && avgAway != null && avgHome != null ? avgAway + avgHome : null;

  return {
    away: { qty: awayQty, costCents: awayCost, avgCents: avgAway, openFeesCents: awayFees },
    home: { qty: homeQty, costCents: homeCost, avgCents: avgHome, openFeesCents: homeFees },
    pairedContracts: paired,
    unpairedContracts: unpaired,
    heavySide,
    blendedPairCostCents: blended,
    grossPairedEdgeCents: blended != null ? paired * (100 - blended) : 0,
    openRiskCents: unpaired * heavyAvg,
    openUpsideCents: unpaired > 0 && heavySide != null ? unpaired * (100 - heavyAvg) : 0,
  };
}

/** Where a paired fee figure came from. `actual` is what Kalshi billed this
 *  trader; `estimate` is the schedule's maker-rate guess, which a caller must
 *  say so on the face — the two can differ by 4× on entries that crossed. */
export type PairedFeeBasis = 'actual' | 'estimate';

export interface PairedEntryFee {
  cents: number;
  basis: PairedFeeBasis;
}

/**
 * Entry fees carried by the PAIRED share, in cents (#512) — what a caller
 * subtracts from `grossPairedEdgeCents` to show a net figure.
 *
 * Entry fees are the whole fee story for a pair HELD TO SETTLEMENT, which is
 * the card's premise (each pair redeems at 100¢) since Kalshi charges nothing
 * at settlement. Selling to close instead pays sell-side fees this never sees.
 *
 * Two sources, and which one answered matters enough to return:
 *
 *  • `actual` — the fees Kalshi really billed, pro-rated from each outcome's
 *    open share down to the paired share by contract count. Preferred: it
 *    matches the trader's statement, and it needs no guess about whether a
 *    fill was maker or taker (a position row doesn't carry the role).
 *  • `estimate` — the caller's rate through the quadratic formula, one leg per
 *    outcome, each priced at its own average and ceiled separately, since the
 *    legs are separate executions billed up to the cent each (the
 *    estimatePortionFee idiom in views/MyTrades). Symmetric in P × (1−P), so a
 *    leg held as NO on the sibling market costs the same as the YES it
 *    normalizes to. Used only when actual fees are incomplete, and a caller
 *    must label the result as an estimate rather than pass it off as net.
 *
 * Rates are supplied by the caller rather than looked up here, which keeps
 * this pure and lets the maker-vs-taker convention live at the call site.
 *
 * Returns `null` only when BOTH sources fail — no actual fees and no usable
 * rate (schedule unloaded, unknown series, or `flat`). Null means UNKNOWN,
 * never zero: a caller must keep showing gross and say so, not print a net
 * figure that silently assumed the trade was free. Nothing paired → 0, so an
 * unpaired position never reports unknown fees on a figure it doesn't display.
 */
export function pairedEntryFeeCents(args: {
  summary: GamePairSummary;
  awayFeeRate: number | null;
  homeFeeRate: number | null;
}): PairedEntryFee | null {
  const { summary, awayFeeRate, homeFeeRate } = args;
  const { away, home } = summary;
  const count = summary.pairedContracts;
  if (count <= 0) return { cents: 0, basis: 'actual' };
  if (away.avgCents == null || home.avgCents == null) return null;

  // Actual fees cover the whole OPEN share; only the paired part of it is this
  // figure's to answer for, so each outcome contributes by contract count.
  // Lopsided quantities make one outcome fully paired and the other partly.
  if (away.openFeesCents != null && home.openFeesCents != null) {
    return {
      cents:
        away.openFeesCents * (count / away.qty) + home.openFeesCents * (count / home.qty),
      basis: 'actual',
    };
  }

  if (awayFeeRate == null || homeFeeRate == null) return null;
  return {
    cents:
      estimateAggregateFeeCents({ priceCents: away.avgCents, count, feeRate: awayFeeRate }) +
      estimateAggregateFeeCents({ priceCents: home.avgCents, count, feeRate: homeFeeRate }),
    basis: 'estimate',
  };
}
